import { Paper } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSetRecoilState } from 'recoil';
import Emoji from '../../components/ui/Emoji';
import { DialogMessageState } from '../../recoil/states';
import { asyncReqCommunityReplyReg } from '../../reducers/CommunityReplyRegReducer';
import { AppDispatch, RootState } from '../../Store';
import CommonUtil from '../../utils/CommonUtil';
import { useKeyboardAwareFocus } from '../../hooks/useKeyboardAwareFocus';

type Props = {
    comment_seq?: string;
}

const ReplyReg = ({ comment_seq = "" }: Props) => {

    const { MEM_UDID, NICKNAME } = useSelector((state: RootState) => state.LoginBtnsReducer.asyncIsLogin); // '로그인 / 로그아웃' 버튼 표시 처리를 위한 state
    const setDialogMessageState = useSetRecoilState(DialogMessageState); // 다일얼로그 메세지 요청 정보를 갖는 state
    const dispatch = useDispatch<AppDispatch>();

    const [iptNicknameVal, setIptNicknameVal] = useState(""); // 별명 입력박스 State
    const [iptAnonymousVal, setIptAnonymousVal] = useState(true); // 별명 익명으로 표시 여부 체크박스 State

    const refBtnReg = useRef<HTMLAnchorElement>(null); // 댓글등록 링크 버튼 참조 객체

    // 입력박스에 포커스가 들어가면 소프트키패드 상단으로 이동 / 해당 입력박스가 가시영역에서 사라지면 소프트키패드 숨김 처리해주는 hook
    useKeyboardAwareFocus({ minVisiblePx: 24, autoBlurIfOutOfView: true });

    (window as any).removePreviewFile(comment_seq); // 첨부파일 초기화

    /**
    * 별명 입력박스 change 이벤트
    * 
    * @param {React.ChangeEvent<HTMLInputElement>} e 이벤트 객체
    * @returns {void}
    */
    const handlerNicknameVal = (e: React.ChangeEvent<HTMLInputElement>): void => {

        setIptNicknameVal(e.currentTarget.value);
    }

    /**
    * 별명 익명으로 표시 체크박스 change 이벤트
    * 
    * @param {React.ChangeEvent<HTMLInputElement>} e 이벤트 객체
    * @returns {void}
    */
    const handlerAnonymousVal = (e: React.ChangeEvent<HTMLInputElement>): void => {

        setIptAnonymousVal(e.currentTarget.checked);
    }

    /**
    * TextBox 폼 체크
    * @param {HTMLInputElement | null} refObj ref 객체
    * @param {string} name ref 객체명
    * @returns {boolean}
    */
    const TextBoxConfirm = (refObj: HTMLInputElement | null, name: string): boolean => {

        if (refObj?.value.trim() === "") {

            const message = name + " 입력하세요.";
            const callbackFuncOk = () => {
                refObj.value = "";
                refObj.focus();
            };
            setDialogMessageState(rawState => ({ ...rawState, isShow: true, isConfirm: false, message, callbackFuncOk }));

            return false;
        }
        return true;
    }

    /**
    * 댓글등록 입력박스 클릭 이벤트 
    * 
    * @param {React.MouseEvent<HTMLInputElement, MouseEvent>} e 이벤트 객체
    * @returns {void}
    */
    const handlerCommentInptClick = (e: React.MouseEvent<HTMLInputElement, MouseEvent>): void => {

        if (MEM_UDID === "") {
            const message = "로그인 후 이용 가능합니다.";
            setDialogMessageState(rawState => ({ ...rawState, isShow: true, isConfirm: false, message }));
            $(e.target).trigger("blur");
            return;
        }
    }

    /**
    * 댓글등록 버튼 클릭 이벤트 
    * 
    * @param {React.MouseEvent<HTMLAnchorElement, MouseEvent>} e 이벤트 객체
    * @returns {void}
    */
    const handlerReplyReg = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>): void => {
        e.preventDefault();

        if (MEM_UDID === "") {
            const message = "로그인 후 이용 가능합니다.";
            setDialogMessageState(rawState => ({ ...rawState, isShow: true, isConfirm: false, message }));
            return;
        }

        const txt_Reg_Nickname = $("#txt_Reg_Nickname_" + comment_seq);
        const txt_Reg_Comment = $("#txt_Reg_Comment_" + comment_seq);

        const board_seq = CommonUtil.getQuerystring("board_seq", "");
        const ref_comment_seq = comment_seq;
        const nickname = iptNicknameVal.trim();
        const comment = ((txt_Reg_Comment.val() as string) + "").trim();
        const anonymous_yn = iptAnonymousVal ? "Y" : "N";

        if (txt_Reg_Nickname.is(":visible") && nickname === "") {
            TextBoxConfirm(txt_Reg_Nickname.get(0) as HTMLInputElement, "'별명'을");
            return;
        }
        if (comment === "") {
            TextBoxConfirm(txt_Reg_Comment.get(0) as HTMLInputElement, "'댓글 내용'을");
            return;
        }

        dispatch(asyncReqCommunityReplyReg({ board_seq, ref_comment_seq, nickname, comment, anonymous_yn, file: (window as any).getPreviewFile(comment_seq) ?? undefined }));
    }

    /**
    * 이모지 표시 버튼 클릭 이벤트
    * 
    * @param {React.MouseEvent<HTMLAnchorElement, MouseEvent>} e 이벤트 객체
    * @returns {void}
    */
    const handlerShowEmoji = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>): void => {

        if (MEM_UDID === "") {
            const message = "로그인 후 이용 가능합니다.";
            setDialogMessageState(rawState => ({ ...rawState, isShow: true, isConfirm: false, message }));
            return;
        }

        // 이모지 영역 표시 or 숨김
        const root_emoji = $("#root_emoji_" + comment_seq);
        if (root_emoji.is(":visible")) root_emoji.hide();
        else root_emoji.show();

        root_emoji.find(".content_emoji").animate({ scrollTop: 0 }, 0); // 이모지 최상단으로 스크롤바 이동
    }

    /**
    * 입력창 엔터 키 체크 이벤트
    * 
    * @param e 이벤트 객체
    * @returns 
    */
    const handlerInputEnterProc = (e: React.KeyboardEvent<HTMLInputElement>) => {

        const btnEl: HTMLAnchorElement | null = refBtnReg.current;
        if (e.key === "Enter") {
            $(e.target).trigger("blur");
            btnEl?.click();
        }
    }

    return (
        <aside className="bo_vc_w">
            <div className="bo_vc_w_info" style={navigator.userAgent.indexOf(";UGGIYO_APP;") > -1 && NICKNAME === "" ? {} : { display: "none" }}>
                <input type="text" className="wr_name frm_input required" id={"txt_Reg_Nickname_" + comment_seq} maxLength={15} placeholder="별명" value={iptNicknameVal} onChange={handlerNicknameVal} />
            </div>

            <div className="bo_vc_w_info">
                <input id={"ipt_anonymous_" + comment_seq} type="checkbox" checked={iptAnonymousVal} style={{ width: "25px", height: "25px" }} onChange={handlerAnonymousVal} /> 별명을 '익명'으로 표시합니다.
            </div>

            <div>
                <div style={{ width: "100%", height: "10px" }}>&nbsp;</div>

                <input id={"file_uploader_" + comment_seq} type="file" width="100%" accept="image/*" data-key={comment_seq} />
                <div id={"preview_root_" + comment_seq} style={{ display: "none" }}><Paper id={"preview_" + comment_seq} sx={{ maxWidth: { md: "350px", xs: "100%" }, border: "1px solid #000000", marginTop: "10px" }} elevation={0} component="img" className="img_add_file" alt="" /></div>
                <div style={{ color: "#1b1613ff", fontWeight: "bold", marginTop: "10px" }}>* 이미지 파일만 업로드 가능합니다.</div>
            </div>

            <div style={{ width: "100%", height: "10px" }}>&nbsp;</div>

            <input type="text"
                className="wr_name frm_input required"
                id={"txt_Reg_Comment_" + comment_seq}
                maxLength={100}
                onClick={handlerCommentInptClick}
                onKeyDown={handlerInputEnterProc}
                placeholder="댓글내용을 입력해주세요."
                style={{ width: "100%" }}
                enterKeyHint="done"
            />
            <div className="bo_vc_w_wr">
                <a className="btn_submit" ref={refBtnReg} onClick={handlerReplyReg}>댓글등록</a>

                <button type="button" className="btn_emoji" onClick={handlerShowEmoji}>
                    <div>
                        <svg aria-label="이모티콘" fill="#262626" height="24" role="img" viewBox="0 0 48 48" width="24">
                            <path d="M24 48C10.8 48 0 37.2 0 24S10.8 0 24 0s24 10.8 24 24-10.8 24-24 24zm0-45C12.4 3 3 12.4 3 24s9.4 21 21 21 21-9.4 21-21S35.6 3 24 3z"></path>
                            <path d="M34.9 24c0-1.4-1.1-2.5-2.5-2.5s-2.5 1.1-2.5 2.5 1.1 2.5 2.5 2.5 2.5-1.1 2.5-2.5zm-21.8 0c0-1.4 1.1-2.5 2.5-2.5s2.5 1.1 2.5 2.5-1.1 2.5-2.5 2.5-2.5-1.1-2.5-2.5zM24 37.3c-5.2 0-8-3.5-8.2-3.7-.5-.6-.4-1.6.2-2.1.6-.5 1.6-.4 2.1.2.1.1 2.1 2.5 5.8 2.5 3.7 0 5.8-2.5 5.8-2.5.5-.6 1.5-.7 2.1-.2.6.5.7 1.5.2 2.1 0 .2-2.8 3.7-8 3.7z"></path>
                        </svg>
                    </div>
                </button>

                <Emoji comment_seq={comment_seq} />
            </div>
        </aside>
    )
}

export default ReplyReg;
